import { HdBodygraphPlanetaryActivation } from '../bodygraph/hd-bodygraph.models';
import { HD_ZODIAC_SIGN_ORDER } from '../bodygraph/hd-gate-coordinates';
import { getHdPlanetDisplayConfigByPlanetId, HdPlanetKey } from '../bodygraph/planet-grid/hd-planet-display-config';
import {
  HD_FIXED_STARS,
  HdFixedStarDefinition,
  HdFixedStarPosition,
} from './hd-fixed-stars.metadata';

/**
 * Precessie-rate uit de 66 Sides Fixed Star Guide (Naraya Fox): 0.838
 * boogminuten per jaar, oftewel 1° per 72 jaar. (De titelpagina van de guide
 * vermeldt eenmalig 0.828'; het uitgewerkte rekenvoorbeeld en de toelichting
 * gebruiken 0.838' — wat ook de astronomische precessie van ~50.3"/jaar is.)
 */
export const HD_FIXED_STAR_PRECESSION_ARCMIN_PER_YEAR = 0.838;

/** De sterposities in de metadata gelden voor het jaar 2000. */
export const HD_FIXED_STAR_EPOCH_YEAR = 2000;

/** Orb voor de "extra" fixed stars (alle planeetactivaties, beide kanten). */
export const HD_FIXED_STAR_EXTRA_ORB_DEGREES = 2;

/** Absolute eclipticale lengte (graden) van een 2000-sterpositie. */
export function hdFixedStarLongitude2000(position: HdFixedStarPosition): number {
  return HD_ZODIAC_SIGN_ORDER.indexOf(position.sign) * 30 + position.degree + position.minute / 60;
}

/**
 * Precessie-correctie in graden die bij een chart-planeetlengte wordt
 * OPGETELD om haar naar de 2000-sterposities te brengen (guide-methode:
 * "advance the planets forward to catch up with the star positions for
 * 2000", of achterwaarts voor geboortes na 2000). Het geboortejaar mag
 * fractioneel zijn.
 */
export function hdPrecessionCorrectionDegrees(birthYear: number): number {
  return ((HD_FIXED_STAR_EPOCH_YEAR - birthYear) * HD_FIXED_STAR_PRECESSION_ARCMIN_PER_YEAR) / 60;
}

/** Cirkelafstand tussen twee eclipticale lengtes, in graden (0..180). */
export function hdCircularDistanceDegrees(a: number, b: number): number {
  const diff = Math.abs(a - b) % 360;
  return diff > 180 ? 360 - diff : diff;
}

/** Eén ster-match: de ster, de gebruikte positie-variant en de afstand in graden. */
export interface HdFixedStarMatch {
  readonly star: HdFixedStarDefinition;
  readonly position: HdFixedStarPosition;
  readonly distanceDegrees: number;
}

/**
 * De dichtstbijzijnde fixed star bij een (al precessie-gecorrigeerde)
 * eclipticale lengte, over alle posities van alle 66 sides. Bij een exacte
 * tie (de drie naam-duo's delen posities) wint het laagste side-nummer;
 * het duo wordt in de UI via `sharesPositionWithSide` mee vermeld.
 */
export function hdFindNearestFixedStar(correctedLongitude: number): HdFixedStarMatch {
  let best: HdFixedStarMatch | null = null;
  for (const star of HD_FIXED_STARS) {
    for (const position of star.positions) {
      const distance = hdCircularDistanceDegrees(correctedLongitude, hdFixedStarLongitude2000(position));
      if (best === null || distance < best.distanceDegrees) {
        best = { star, position, distanceDegrees: distance };
      }
    }
  }
  return best as HdFixedStarMatch;
}

/**
 * Alle fixed stars binnen de gegeven orb rond een (gecorrigeerde) lengte,
 * dichtstbijzijnde eerst. Per side telt enkel de dichtstbijzijnde positie.
 */
export function hdFindFixedStarsWithinOrb(
  correctedLongitude: number,
  orbDegrees: number,
): HdFixedStarMatch[] {
  const matches: HdFixedStarMatch[] = [];
  for (const star of HD_FIXED_STARS) {
    let best: HdFixedStarMatch | null = null;
    for (const position of star.positions) {
      const distance = hdCircularDistanceDegrees(correctedLongitude, hdFixedStarLongitude2000(position));
      if (distance <= orbDegrees && (best === null || distance < best.distanceDegrees)) {
        best = { star, position, distanceDegrees: distance };
      }
    }
    if (best) {
      matches.push(best);
    }
  }
  return matches.sort((a, b) => a.distanceDegrees - b.distanceDegrees);
}

/** De primaire fixed star: dichtstbijzijnde ster bij de design-noordknoop. */
export interface HdPrimaryFixedStarResult extends HdFixedStarMatch {
  /** Ruwe chart-lengte van de design-noordknoop. */
  readonly activationLongitude: number;
  /** Lengte na precessie-correctie (waarmee vergeleken is). */
  readonly correctedLongitude: number;
  /** Poort.lijn van de design-noordknoop in de chart. */
  readonly gate: number;
  readonly line: number;
}

/** Eén extra-ster-match, gekoppeld aan de activatie die hem opleverde. */
export interface HdExtraFixedStarResult extends HdFixedStarMatch {
  readonly planetKey: HdPlanetKey;
  readonly side: 'design' | 'personality';
  readonly activationLongitude: number;
  readonly correctedLongitude: number;
  /** Poort.lijn van de activatie in de chart. */
  readonly gate: number;
  readonly line: number;
}

export interface HdFixedStarsResult {
  /** Toegepaste precessie-correctie in graden (positief = opgeteld). */
  readonly precessionCorrectionDegrees: number;
  readonly birthYear: number;
  /** `null` wanneer de chart geen design-noordknoop-activatie met lengte heeft. */
  readonly primary: HdPrimaryFixedStarResult | null;
  /**
   * Sterren binnen {@link HD_FIXED_STAR_EXTRA_ORB_DEGREES} van eender welke
   * planeetactivatie van de chart (beide kanten). De primaire
   * design-noordknoop-match zelf wordt hier niet herhaald.
   */
  readonly extras: readonly HdExtraFixedStarResult[];
}

function planetKeyOf(activation: HdBodygraphPlanetaryActivation): HdPlanetKey | null {
  return getHdPlanetDisplayConfigByPlanetId(activation.planetId)?.key ?? null;
}

/** Chartloze placeholder-activaties (alles 0) dragen geen bruikbare lengte. */
function hasUsableLongitude(activation: HdBodygraphPlanetaryActivation): boolean {
  return activation.longitude !== 0 || activation.color > 0 || activation.tone > 0 || activation.base > 0;
}

/**
 * Bepaalt de fixed stars van een chart volgens de 66 Sides-methode:
 *  1. corrigeer de planeetlengtes voor precessie naar het jaar 2000
 *     ({@link hdPrecessionCorrectionDegrees});
 *  2. de primaire fixed star is de 66-sides-ster die het dichtst bij de
 *     design-noordknoop staat;
 *  3. extra sterren: elke ster binnen 2° orb van eender welke
 *     planeetactivatie, aan beide kanten (personality + design).
 *
 * Met `includeExtras: false` blijft stap 3 achterwege (lege `extras`): het
 * Geavanceerd-paneel toont de extras pas op aanvraag en rekent ze dan pas uit.
 */
export function resolveHdFixedStars(
  activations: readonly HdBodygraphPlanetaryActivation[],
  birthYear: number,
  options: { readonly includeExtras?: boolean } = {},
): HdFixedStarsResult {
  const correction = hdPrecessionCorrectionDegrees(birthYear);

  const designNorthNode = activations.find(
    activation => activation.side === 'design'
      && planetKeyOf(activation) === 'northNode'
      && hasUsableLongitude(activation),
  );

  let primary: HdPrimaryFixedStarResult | null = null;
  if (designNorthNode) {
    const corrected = designNorthNode.longitude + correction;
    primary = {
      ...hdFindNearestFixedStar(corrected),
      activationLongitude: designNorthNode.longitude,
      correctedLongitude: corrected,
      gate: designNorthNode.gate,
      line: designNorthNode.line,
    };
  }

  const extras: HdExtraFixedStarResult[] = [];
  if (options.includeExtras === false) {
    return { precessionCorrectionDegrees: correction, birthYear, primary, extras };
  }
  for (const activation of activations) {
    const planetKey = planetKeyOf(activation);
    if (planetKey === null) {
      continue;
    }
    if ((activation.side !== 'design' && activation.side !== 'personality') || !hasUsableLongitude(activation)) {
      continue;
    }
    const corrected = activation.longitude + correction;
    for (const match of hdFindFixedStarsWithinOrb(corrected, HD_FIXED_STAR_EXTRA_ORB_DEGREES)) {
      const isPrimaryDuplicate = primary !== null
        && activation.side === 'design'
        && planetKey === 'northNode'
        && match.star.side === primary.star.side;
      if (isPrimaryDuplicate) {
        continue;
      }
      extras.push({
        ...match,
        planetKey,
        side: activation.side,
        activationLongitude: activation.longitude,
        correctedLongitude: corrected,
        gate: activation.gate,
        line: activation.line,
      });
    }
  }
  extras.sort((a, b) => a.distanceDegrees - b.distanceDegrees);

  return { precessionCorrectionDegrees: correction, birthYear, primary, extras };
}

/** Formatteert een afstand in graden als `d°mm'` (bv. `0°17'`). */
export function formatHdFixedStarDistance(distanceDegrees: number): string {
  const totalMinutes = Math.round(distanceDegrees * 60);
  const degrees = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${degrees}°${String(minutes).padStart(2, '0')}'`;
}
